import { History, Search, UserPlus, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { usePosCustomerSearch } from '@/components/pos/usePosCustomerSearch'
import { PosNewGuestModal } from '@/components/pos/PosNewGuestModal'
import { useBranch } from '@/contexts/BranchContext'
import {
  defaultPhoneCountryForBranch,
  formatE164Display,
  formatLocalPhoneInput,
  GCC_COUNTRY_CODES,
  PHONE_LOCAL_PLACEHOLDER,
} from '@/lib/phone'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

import { isCustomerBlacklisted } from './posCustomer'

/**
 * @param {object} props
 * @param {(customer: import('@/types/api').Customer) => void} props.onSelectCustomer
 * @param {boolean} props.disabled
 * @param {string} [props.error]
 */
export function PosCustomerSearch({ onSelectCustomer, disabled, error: externalError }) {
  const inputRef = useRef(null)
  const [newGuestOpen, setNewGuestOpen] = useState(false)
  const { activeBranch } = useBranch()
  const phoneCountry = defaultPhoneCountryForBranch(activeBranch?.country)
  const {
    query,
    setQuery,
    results,
    searching,
    error,
    phoneE164,
    exactMatch,
    reset,
    selectCustomer,
  } = usePosCustomerSearch({ onSelect: onSelectCustomer, phoneCountry })

  const displayError = externalError || error

  const openNewGuest = () => setNewGuestOpen(true)

  const handleRegistered = (customer) => {
    setNewGuestOpen(false)
    onSelectCustomer(customer)
    reset()
  }

  return (
    <>
      <div className="space-y-2">
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Search className="h-3 w-3" aria-hidden />
              Customer phone
            </span>
            {!SALON_DESK_CHECKOUT.lite ? (
              <kbd className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[9px]">F2</kbd>
            ) : null}
          </span>
          <div className="flex gap-1.5">
            <select
              value={phoneCountry}
              disabled
              aria-label="Country code"
              className="w-[4.5rem] shrink-0 rounded-xl border border-input bg-muted/40 px-1.5 py-2 text-[10px] font-semibold text-muted-foreground"
              title="Branch default country"
            >
              {GCC_COUNTRY_CODES.filter((item) => item.code === phoneCountry).map((country) => (
                <option key={country.code} value={country.code}>
                  {country.label}
                </option>
              ))}
            </select>
            <input
              ref={inputRef}
              id="pos-customer-search"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              disabled={disabled}
              value={query}
              onChange={(event) => setQuery(formatLocalPhoneInput(event.target.value, phoneCountry))}
              placeholder={PHONE_LOCAL_PLACEHOLDER}
              className="min-w-0 flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
          </div>
        </label>

        {displayError ? (
          <p className="text-[11px] text-destructive">{displayError}</p>
        ) : null}

        {searching ? (
          <p className="text-[11px] text-muted-foreground">Searching…</p>
        ) : null}

        {results.length > 0 ? (
          <ul className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-border/50 bg-card p-1">
            {results.map((customer) => (
              <li key={customer.id}>
                <button
                  type="button"
                  disabled={disabled || isCustomerBlacklisted(customer)}
                  onClick={() => selectCustomer(customer)}
                  className={cn(
                    'flex w-full flex-col rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50',
                    exactMatch?.id === customer.id && 'bg-primary/5 ring-1 ring-primary/20',
                  )}
                >
                  <span className="text-xs font-semibold">{customer.name}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {formatE164Display(customer.phone)}
                  </span>
                  {isCustomerBlacklisted(customer) ? (
                    <span className="mt-0.5 text-[10px] font-medium text-destructive">Blacklisted</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <button
          type="button"
          disabled={disabled}
          onClick={openNewGuest}
          className="flex w-full items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-primary disabled:opacity-50"
        >
          <UserPlus className="h-3 w-3" aria-hidden />
          New guest
        </button>
      </div>

      <PosNewGuestModal
        open={newGuestOpen}
        onClose={() => setNewGuestOpen(false)}
        onRegistered={handleRegistered}
        phoneCountry={phoneCountry}
        initialPhone={query}
        disabled={disabled}
      />
    </>
  )
}

/**
 * @param {object} props
 * @param {import('@/types/api').Customer} props.customer
 * @param {() => void} props.onClear
 * @param {() => void} [props.onOpenHistory]
 * @param {boolean} props.disabled
 */
export function PosCustomerCard({ customer, onClear, onOpenHistory, disabled }) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-primary/8 to-gold/10 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{customer.name}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {formatE164Display(customer.phone)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onOpenHistory ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onOpenHistory}
              className="flex h-7 items-center gap-1 rounded-lg px-2 text-[10px] font-semibold uppercase tracking-wide text-primary hover:bg-card disabled:opacity-50"
              title="Visit history"
            >
              <History className="h-3.5 w-3.5" aria-hidden />
              History
            </button>
          ) : null}
          <button
            type="button"
            disabled={disabled}
            onClick={onClear}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-50"
            aria-label="Clear guest"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {customer.preferred_stylist?.name ? (
        <p className="mt-2 text-[10px] text-muted-foreground">
          Preferred stylist: {customer.preferred_stylist.name}
        </p>
      ) : null}
    </div>
  )
}
